/**
 * Session — 单个会话：transcript + 发消息 + 权限确认卡。
 *
 * transcript 由 useCoworkSession 驱动 —— dispatch relay 的事件流经 shared
 * streamDecoder（@cowork/streamDecoder，和 desktop 同一个）fold 成
 * CortexMessage[]，本页只负责渲染（手机紧凑视图）。
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ApiError, sendMessage, sendVoiceMessage, type AttachmentRef } from '../lib/api'
import { useCoworkSession, type DispatchSessionState } from '../hooks/useCoworkSession'
import { useDraft } from '../lib/useDraft'
import { useLang } from '../contexts/LanguageContext'
import { Spinner } from '../components/ui'
import Composer from '../components/Composer'
import Message from '../components/Message'
import { IconBack, IconAlert } from '../components/icons'

const STATE_META: Record<DispatchSessionState, { labelKey: string; tone: string }> = {
  idle: { labelKey: 'session.stateIdle', tone: 'text-dimmed' },
  processing: { labelKey: 'session.stateProcessing', tone: 'text-accent-light' },
  streaming: { labelKey: 'session.stateStreaming', tone: 'text-accent-light' },
  awaiting_user: { labelKey: 'session.stateAwaitingUser', tone: 'text-warning' },
  completed: { labelKey: 'session.stateCompleted', tone: 'text-success' },
  errored: { labelKey: 'session.stateErrored', tone: 'text-error' },
  paused: { labelKey: 'session.statePaused', tone: 'text-dimmed' },
}

export default function Session() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useLang()

  const view = useCoworkSession(id, () => navigate('/pair', { replace: true }))

  const [draft, setDraft] = useDraft(`session:${id ?? 'unknown'}`)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [view.messages.length])

  const handleSend = async (attachments: AttachmentRef[] = []) => {
    if (!id || (!draft.trim() && attachments.length === 0) || sending) return
    setSending(true)
    setSendError(null)
    try {
      await sendMessage(id, draft.trim(), attachments)
      setDraft('')
    } catch (e) {
      setSendError(e instanceof ApiError && e.status === 409
        ? t('session.offlineHint')
        : (e as Error)?.message?.slice(0, 200) || t('space.sendFailed'))
    } finally {
      setSending(false)
    }
  }

  const handleSendVoice = async (audio: Blob, onProgress: (frac: number) => void) => {
    if (!id) return
    setSendError(null)
    try {
      await sendVoiceMessage(id, audio, onProgress)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setSendError(t('session.offlineHint'))
      else if (e instanceof ApiError && e.status === 413) setSendError(t('voice.tooLong'))
      else setSendError((e as Error)?.message?.slice(0, 200) || t('space.sendFailed'))
    }
  }

  const meta = STATE_META[view.state]
  const busy = view.state === 'processing' || view.state === 'streaming'
  const error = view.error || sendError
  const ra = view.requiresAction

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-line-subtle bg-canvas/92 px-2 pb-3 pr-4 pt-safe backdrop-blur">
        <button
          onClick={() => navigate(-1)}
          aria-label={t('common.back')}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-muted active:bg-hover"
        >
          <IconBack className="h-5 w-5" />
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 text-xs">
          <span
            className={`h-1.5 w-1.5 rounded-full ${view.sseConnected ? 'bg-success' : 'bg-faint'}`}
            title={view.sseConnected ? t('session.online') : t('session.reconnecting')}
          />
          <span className={meta.tone}>{t(meta.labelKey)}</span>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4 pb-36">
        {error && (
          <div className="reveal rounded-lg border border-error/30 bg-error/10 px-3.5 py-3 text-sm text-error">
            {error}
          </div>
        )}
        {(!view.loaded || (view.replayPending && view.messages.length === 0)) && !error && (
          <div className="flex flex-col items-center gap-2 py-12">
            <Spinner className="h-6 w-6 text-accent" />
            {view.replayPending && <span className="text-xs text-dimmed">{t('session.loadingHistory')}</span>}
          </div>
        )}
        {view.loaded && !view.replayPending && view.messages.length === 0 && !error && (
          <div className="reveal-in py-16 text-center text-sm text-dimmed">{t('session.noMessages')}</div>
        )}
        {view.messages.map((m) => (
          <Message key={m.id} message={m} />
        ))}
        {busy && (
          <div className="flex items-center gap-2 px-1 py-1 text-xs text-accent-light">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-light breathe" />
            {t('session.running')}
          </div>
        )}
      </div>

      {ra && (
        <div className="reveal fixed inset-x-3 bottom-[5.5rem] z-20 rounded-xl border border-accent/35 bg-elevated p-4 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-accent-light">
            <IconAlert className="h-4 w-4" />
            {t('session.permTitle')}
          </div>
          <div className="mb-1 text-xs text-muted">{ra.tool_name}</div>
          <div className="mb-3 break-words text-sm text-primary">{ra.action_description}</div>
          {ra.raw_command && (
            <code className="mb-3 block break-words rounded-md bg-canvas px-2.5 py-2 font-mono text-[11px] text-success">
              {ra.raw_command}
            </code>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => void view.resolvePermission('allow')}
              className="flex-1 rounded-lg bg-accent py-2.5 text-sm font-medium text-white min-h-tap"
            >
              {t('session.allow')}
            </button>
            <button
              onClick={() => void view.resolvePermission('allow_always')}
              className="flex-1 rounded-lg border border-accent/40 py-2.5 text-sm text-accent-light min-h-tap"
            >
              {t('session.allowAlways')}
            </button>
            <button
              onClick={() => void view.resolvePermission('deny')}
              className="flex-1 rounded-lg border border-line py-2.5 text-sm text-error min-h-tap"
            >
              {t('session.deny')}
            </button>
          </div>
        </div>
      )}

      <footer
        className="fixed inset-x-0 bottom-0 z-10 border-t border-line-subtle bg-canvas/95 px-3 pt-2.5 backdrop-blur"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.625rem)' }}
      >
        <Composer
          value={draft}
          onChange={setDraft}
          onSend={(atts) => void handleSend(atts)}
          onSendVoice={handleSendVoice}
          sending={sending}
          placeholder={t('session.inputPlaceholder')}
          draftKey={`session:${id ?? 'unknown'}`}
        />
      </footer>
    </div>
  )
}
